import { type FC, useState } from 'react';

import { useFundEsgQuery } from '@/api/esg/esgApi';
import { Column, Row } from '@/components/Grid';
import { ConnectPopover } from '@/components/Pretzel/ConnectPopover';
import { PushRight } from '@/components/PushRight';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { useUserLocale } from '@/i18n/hooks';
import { CurrencyCode } from '@/types/CurrencyCode';

import { CarbonIntensityChart } from '../components/CarbonIntensityChart';
import { CurrencyChips } from '../components/CurrencyChips';
import { LargeText } from '../components/LargeText';
import { SpaceBetween } from '../components/SpaceBetween';

interface Props {
  isin: string;
}

export const EsgCarbonIntensities: FC<Props> = ({ isin }) => {
  const texts = useTexts();
  const esgFundQuery = useFundEsgQuery({ isin });
  const locale = useUserLocale();

  const [currency, setCarbonIntensityCurrency] = useState<CurrencyCode>(
    esgFundQuery.data!.carbonIntensities[0]?.currency,
  );

  const carbonIntensity = esgFundQuery.data!.carbonIntensities.find(
    (carbon) => carbon.currency === currency,
  );

  return (
    <WithGap>
      <SpaceBetween>
        <WithGap gap="8" direction="row">
          <h2>{texts.pages.fundEsg.carbonIntensities.title}</h2>

          {texts.pages.fundEsg.carbonIntensities.helpDescription && (
            <ConnectPopover
              id="fund-esg-carbon-intensities"
              description={
                texts.pages.fundEsg.carbonIntensities.helpDescription
              }
            />
          )}
        </WithGap>

        <CurrencyChips
          currencies={esgFundQuery.data!.carbonIntensities.map(
            (carbon) => carbon.currency,
          )}
          currency={currency}
          label={texts.pages.fundEsg.carbonIntensities.currency}
          onChange={(currency) => {
            setCarbonIntensityCurrency(currency);
          }}
        />
      </SpaceBetween>

      <Row>
        <Column $span={6}>
          <WithGap gap="16">
            <LargeText>{`${carbonIntensity?.kpi.value} ${texts.pages.esg.carbonFootprint.tonnage}`}</LargeText>

            <div className="font-body-demibold">{`CO2e/M${currency}`}</div>

            <p className="font-body">
              {fmsWithTemplate(
                texts.pages.fundEsg.carbonIntensities.description,
                {
                  currency,
                },
              )}
            </p>
          </WithGap>
        </Column>

        <Column $span={6}>
          {carbonIntensity?.benchmark && (
            <CarbonIntensityChart
              fundValue={carbonIntensity?.kpi.value}
              benchmarkValue={carbonIntensity?.benchmark.value}
            />
          )}
        </Column>
      </Row>

      <PushRight>
        <WithGap gap="16" direction="row">
          <div className="font-details">
            {texts.pages.fundEsg.fund.coverage}{' '}
            {carbonIntensity?.kpi.coverageInPercent.toLocaleString(locale)}%
          </div>

          {carbonIntensity?.benchmark && (
            <div className="font-details">
              {texts.pages.fundEsg.benchmark.coverage}{' '}
              {carbonIntensity.benchmark.coverageInPercent.toLocaleString(
                locale,
              )}
              %
            </div>
          )}
        </WithGap>
      </PushRight>
    </WithGap>
  );
};
